import { useCallback, useState } from 'react';

import ROUTES from '@/common/routes';
import { SnackbarState } from '@/modules/core/core.types';
import useHistory from '@/modules/core/hooks/useHistory';
import useSnackbar from '@/modules/core/hooks/useSnackbar';
import { usePayoutDefinitionDeleteModalStore } from '@/modules/ProcessingDefinition/PayoutDefinition/PayoutDefinitionDeleteModal/stores/usePayoutDefinitionDeleteModal.store';
import useDeletePayoutDefinitionQuery from '@/modules/ProcessingDefinition/PayoutDefinition/queries/useDeletePayoutDefinition.query';

interface Props {
  payoutDefinitionId: string;
}

const usePayoutDefinitionDeleteModal = ({ payoutDefinitionId }: Props) => {
  const history = useHistory();
  const { handleAddPermanentSnackbar } = useSnackbar();
  const handleChangeVisibility = usePayoutDefinitionDeleteModalStore((state) => state.handleChangeVisibility);
  const modalVisible = usePayoutDefinitionDeleteModalStore((state) => state.modalVisible);
  const [hasLinkedError, setHasLinkedError] = useState(false);

  const { deletePayoutDefinition, isLoading } = useDeletePayoutDefinitionQuery({ payoutDefinitionId });

  const handleClose = useCallback(() => {
    handleChangeVisibility(false);
    setHasLinkedError(false);
  }, [handleChangeVisibility]);

  const handleDelete = useCallback(async () => {
    try {
      await deletePayoutDefinition();
      handleChangeVisibility(false);
      handleAddPermanentSnackbar({
        text: `Payout definition #${payoutDefinitionId} was deleted`,
        state: SnackbarState.negative,
        closeIcon: true,
      });
      history.push(ROUTES.PAYOUT_DEFINITIONS);
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 400) {
        setHasLinkedError(true);
      }
    }
  }, [
    deletePayoutDefinition,
    handleChangeVisibility,
    handleAddPermanentSnackbar,
    payoutDefinitionId,
    history,
  ]);

  return {
    modalVisible,
    hasLinkedError,
    isLoading,
    handleClose,
    handleDelete,
  };
};

export default usePayoutDefinitionDeleteModal;
